import type { Moment } from 'moment';
import moment from 'moment';

import type { Recommendation, TTDCampaign } from '@feathr/blackbox';

// Mirrors the non-monetization branch of getMinBudget in AdWizardBudgetStep.utils.ts
// (days * 5). validateStepBudget enforces this floor when a published campaign's
// dates are dirty. If getMinBudget's rule ever changes, update it there and here.
export const MIN_DAILY_BUDGET_DOLLARS = 5;

export interface IExtendCap {
  maxAllowedMoment: Moment;
  canExtend: boolean;
  effectiveMoment: Moment;
}

export function getExtendCap(campaign: TTDCampaign, recommendation: Recommendation): IExtendCap {
  const currentBudget = campaign.get('exposure_settings').target_value || 0;
  const proposedBudget =
    recommendation.get('proposed_campaign_values')?.exposure_settings?.target_value ?? 0;
  const effectiveBudget = Math.max(currentBudget, proposedBudget);
  const maxTotalDays = Math.floor(effectiveBudget / MIN_DAILY_BUDGET_DOLLARS);

  const dateStart = campaign.get('date_start');
  const proposedDateEnd = recommendation.get('proposed_campaign_values').date_end;
  const recommendedMoment = moment.utc(proposedDateEnd);
  const prevDateEnd = recommendation.get('current_campaign_values').date_end;
  const prevMoment = moment.utc(prevDateEnd);

  // Monetization uses a different floor (days * 4000 impressions) and
  // validateStepBudget skips the $/day check for those. Short-circuit to
  // avoid miscalculating on the $5 assumption.
  // Hours-offset (not .add('days')) so DST cannot perturb getDays' Math.ceil.
  const maxAllowedMoment = campaign.isMonetization
    ? recommendedMoment.clone()
    : moment.utc(dateStart).add(maxTotalDays * 24, 'hours');

  const canExtend = campaign.isMonetization || maxAllowedMoment.isAfter(prevMoment);

  const effectiveMoment = canExtend
    ? moment.min(recommendedMoment.clone(), maxAllowedMoment.clone())
    : prevMoment.clone();

  return { maxAllowedMoment, canExtend, effectiveMoment };
}

// react-datepicker's maxDate disables whole days only. A user picking 23:59
// on the cap-boundary day would tip getDays over to (maxTotalDays + 1) via
// Math.ceil, re-triggering the min-budget error. Clamp here so the model
// only ever sees values within [prevMoment, maxAllowedMoment].
//
// Returns a tz-naive ISO string (no trailing Z). anhinga's Campaign.date_end
// pipeline mixes tz-naive `datetime.utcnow()` comparisons with the parsed
// incoming date, so a tz-aware input triggers "can't compare offset-naive
// and offset-aware datetimes". Format matches what mongoengine round-trips.
export function clampDateEndToCap(
  candidateIso: string,
  campaign: TTDCampaign,
  recommendation: Recommendation,
): string {
  const { maxAllowedMoment } = getExtendCap(campaign, recommendation);
  const prevMoment = moment.utc(recommendation.get('current_campaign_values').date_end);
  const picked = moment.utc(candidateIso);
  const clamped = moment.min(moment.max(picked, prevMoment.clone()), maxAllowedMoment.clone());
  return clamped.utc().format('YYYY-MM-DDTHH:mm:ss.SSS');
}

// True when the saved date_end has reached (or exceeded) the effective
// (capped) recommendation. Comparing against effective — not the raw
// proposal — ensures a capped save still clears the recommendation card.
export function isEffectiveDateEndReached(
  campaign: TTDCampaign,
  recommendation: Recommendation,
): boolean {
  const savedDateEnd = campaign.get('date_end');
  const { effectiveMoment } = getExtendCap(campaign, recommendation);
  return moment.utc(savedDateEnd).isSameOrAfter(effectiveMoment);
}
